'use client';

import { LenisOptions } from 'lenis';
import ReactLenis, { LenisRef } from 'lenis/react';
import { cancelFrame, frame } from 'motion/react';
import { PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import MainContent from '~/components/atoms/containers/MainContent';
import ScrollDirectionNotifier from '~/components/molecules/ScrollDirectionNotifier';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import { lenisPreventCheck } from '~/lib/utils/scroll';

export interface Props extends PropsWithChildren {
  className?: string;
  lenisOptions?: LenisOptions & { autoRaf?: never };
}

const MainLayout: React.FC<Props> = ({ className, lenisOptions, children }) => {
  const lenisRef = useRef<LenisRef>(null);

  const isMobile = useHydrationSafeResponsive('mobile');
  const isHorizontal = lenisOptions?.orientation === 'horizontal';

  const options = useMemo<LenisOptions>(() => {
    const {
      orientation = 'vertical',
      infinite = false,
      ...rest
    } = lenisOptions || {};

    const orientationWithMobile = isMobile ? 'vertical' : orientation;

    return {
      autoRaf: false,
      orientation: orientationWithMobile,
      prevent: lenisPreventCheck,
      smoothWheel: !isMobile, // orientationWithMobile === 'horizontal',
      syncTouch: true,
      syncTouchLerp: 0.075,
      touchInertiaMultiplier: 16,
      overscroll: false,
      infinite: isMobile ? false : infinite,

      ...rest,
    };
  }, [isMobile, lenisOptions]);

  useEffect(() => {
    const update = (data: { timestamp: number }) => {
      const time = data.timestamp;
      lenisRef.current?.lenis?.raf(time);
    };

    frame.update(update, true);

    return () => cancelFrame(update);
  }, []);

  return (
    <ReactLenis ref={lenisRef} options={options} root>
      {isHorizontal && <ScrollDirectionNotifier />}
      <MainContent className={className}>{children}</MainContent>
    </ReactLenis>
  );
};

export default MainLayout;
